import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Paperclip, Trash2, FileText, X } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { useToast } from "@/components/system";
import { formatError } from "@/lib/format-error";

/**
 * BW20.4a — Document attachments UI.
 *
 * Renders the attachments list for a single document and provides upload
 * (drag-drop or button), delete, and a thumbnail lightbox-style preview
 * via the storage bucket's signed URLs.
 *
 * Object keys follow {user_id}/{document_id}/{uuid}-{filename} so the
 * RLS policies on storage.objects (auth.uid() = first folder segment)
 * lock writes to the owner.
 */

const MAX_BYTES = 10 * 1024 * 1024; // 10 MB
const ALLOWED = /^(application\/pdf|image\/(jpeg|jpg|png|heic|heif|webp))$/i;
const SIGNED_URL_TTL_S = 60 * 60;

type AttachmentRow = {
  id: string;
  document_id: string;
  file_path: string;
  file_mime: string;
  file_size_bytes: number;
  caption: string | null;
  uploaded_at: string;
};

export function DocumentAttachments({ documentId }: { documentId: string }) {
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();
  const toast = useToast();
  const [isOver, setIsOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewMime, setPreviewMime] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["document_attachments", documentId],
    enabled: Boolean(documentId && userId),
    queryFn: async (): Promise<AttachmentRow[]> => {
      const { data, error } = await supabase
        .from("document_attachment")
        .select("id, document_id, file_path, file_mime, file_size_bytes, caption, uploaded_at")
        .eq("document_id", documentId)
        .order("uploaded_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as AttachmentRow[];
    },
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (!userId) throw new Error("Not signed in");
      if (file.size > MAX_BYTES) throw new Error("File too large (max 10MB)");
      if (file.type && !ALLOWED.test(file.type)) {
        throw new Error(`Unsupported file type: ${file.type}`);
      }
      const safeName = file.name.replace(/[^A-Za-z0-9._-]+/g, "_");
      const path = `${userId}/${documentId}/${crypto.randomUUID()}-${safeName}`;
      const { error: upErr } = await supabase.storage
        .from("documents")
        .upload(path, file, {
          cacheControl: "3600",
          contentType: file.type || "application/octet-stream",
          upsert: false,
        });
      if (upErr) throw upErr;
      const { error: insErr } = await supabase.from("document_attachment").insert({
        user_id: userId,
        document_id: documentId,
        file_path: path,
        file_mime: file.type || "application/octet-stream",
        file_size_bytes: file.size,
      });
      if (insErr) {
        // Best-effort cleanup so we don't leak orphan blobs in storage.
        await supabase.storage.from("documents").remove([path]).catch(() => undefined);
        throw insErr;
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["document_attachments", documentId] });
      toast.show("Attached.");
    },
    onError: (e) => toast.show(formatError(e)),
  });

  const del = useMutation({
    mutationFn: async (a: AttachmentRow) => {
      const { error: rmErr } = await supabase.storage
        .from("documents")
        .remove([a.file_path]);
      if (rmErr) throw rmErr;
      const { error: dbErr } = await supabase
        .from("document_attachment")
        .delete()
        .eq("id", a.id);
      if (dbErr) throw dbErr;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["document_attachments", documentId] });
      toast.show("Removed.");
    },
    onError: (e) => toast.show(formatError(e)),
  });

  async function openPreview(a: AttachmentRow) {
    const { data, error } = await supabase.storage
      .from("documents")
      .createSignedUrl(a.file_path, SIGNED_URL_TTL_S);
    if (error) {
      toast.show(`Couldn't load preview: ${error.message}`);
      return;
    }
    setPreviewUrl(data?.signedUrl ?? null);
    setPreviewMime(a.file_mime);
  }

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      try {
        await upload.mutateAsync(file);
      } catch {
        // mutateAsync already shows toast on error; keep iterating so a
        // single bad file doesn't drop the rest of the batch.
      }
    }
  }

  return (
    <section
      aria-label="Attachments"
      style={{
        marginTop: "var(--space-6)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-3)",
      }}
    >
      <header style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
        <Paperclip size={14} strokeWidth={1.5} style={{ color: "var(--fg-3)" }} />
        <span
          style={{
            fontSize: "var(--type-micro)",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "var(--fg-3)",
          }}
        >
          Attachments
        </span>
      </header>

      <div
        onDragEnter={(e) => {
          if (Array.from(e.dataTransfer.types ?? []).includes("Files")) {
            e.preventDefault();
            setIsOver(true);
          }
        }}
        onDragOver={(e) => {
          if (Array.from(e.dataTransfer.types ?? []).includes("Files")) {
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
          }
        }}
        onDragLeave={() => setIsOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsOver(false);
          void handleFiles(e.dataTransfer.files);
        }}
        style={{
          padding: "var(--space-4)",
          background: isOver ? "var(--accent-wash)" : "var(--bg-2)",
          border: `2px dashed ${isOver ? "var(--accent)" : "var(--hair-2)"}`,
          borderRadius: "var(--radius-card)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-2)",
          alignItems: "center",
          justifyContent: "center",
          transition: "border-color var(--dur-fast) var(--ease-out), background var(--dur-fast) var(--ease-out)",
        }}
      >
        <p
          style={{
            margin: 0,
            color: "var(--fg-2)",
            fontSize: "var(--type-body-s)",
            textAlign: "center",
          }}
        >
          {isOver
            ? "Drop to upload"
            : "Drop a PDF, JPG, PNG, or HEIC here, or"}
        </p>
        {!isOver && (
          <label
            style={{
              cursor: "pointer",
              padding: "6px 14px",
              background: "var(--accent)",
              color: "#0a0b0d",
              borderRadius: "var(--radius-card)",
              fontSize: "var(--type-body-s)",
              fontWeight: 600,
            }}
          >
            Choose file
            <input
              type="file"
              accept="application/pdf,image/*"
              multiple
              onChange={(e) => {
                void handleFiles(e.target.files);
                e.target.value = "";
              }}
              style={{ display: "none" }}
            />
          </label>
        )}
        <p style={{ margin: 0, color: "var(--fg-3)", fontSize: "var(--type-meta)" }}>
          Up to 10 MB per file.
        </p>
      </div>

      {q.data && q.data.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
            gap: "var(--space-2)",
          }}
        >
          {q.data.map((a) => (
            <AttachmentTile
              key={a.id}
              attachment={a}
              onOpen={() => void openPreview(a)}
              onDelete={() => {
                if (confirm("Remove this attachment?")) del.mutate(a);
              }}
            />
          ))}
        </ul>
      )}

      {previewUrl && (
        <PreviewLightbox
          url={previewUrl}
          mime={previewMime}
          onClose={() => {
            setPreviewUrl(null);
            setPreviewMime(null);
          }}
        />
      )}
    </section>
  );
}

function AttachmentTile({
  attachment,
  onOpen,
  onDelete,
}: {
  attachment: AttachmentRow;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const isImage = /^image\//i.test(attachment.file_mime);
  const filename = attachment.file_path.split("/").pop() ?? "file";
  const sizeKb = Math.round(attachment.file_size_bytes / 1024);

  return (
    <li
      style={{
        position: "relative",
        background: "var(--bg-2)",
        border: "1px solid var(--hair-2)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-2)",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <button
        type="button"
        onClick={onOpen}
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: "pointer",
          textAlign: "left",
          display: "flex",
          flexDirection: "column",
          gap: 6,
          minWidth: 0,
        }}
      >
        <div
          aria-hidden
          style={{
            height: 90,
            background: isImage ? "var(--bg-3)" : "var(--accent-wash)",
            color: "var(--accent)",
            borderRadius: "var(--radius-card)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isImage ? (
            <ImageThumb path={attachment.file_path} />
          ) : (
            <FileText size={28} strokeWidth={1.5} />
          )}
        </div>
        <div
          style={{
            fontSize: "var(--type-meta)",
            color: "var(--fg-2)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {filename}
        </div>
        <div style={{ fontSize: "var(--type-micro)", color: "var(--fg-3)" }}>
          {sizeKb} KB
        </div>
      </button>
      <button
        type="button"
        onClick={onDelete}
        aria-label="Remove attachment"
        style={{
          position: "absolute",
          top: 6,
          right: 6,
          background: "rgba(10,11,13,0.7)",
          border: "none",
          color: "var(--fg)",
          borderRadius: "var(--radius-chip)",
          padding: 4,
          cursor: "pointer",
          display: "inline-flex",
        }}
      >
        <Trash2 size={12} strokeWidth={1.6} />
      </button>
    </li>
  );
}

function ImageThumb({ path }: { path: string }) {
  const q = useQuery({
    queryKey: ["document_attachment_signed", path],
    queryFn: async () => {
      const { data, error } = await supabase.storage
        .from("documents")
        .createSignedUrl(path, SIGNED_URL_TTL_S);
      if (error) throw error;
      return data?.signedUrl ?? null;
    },
    staleTime: SIGNED_URL_TTL_S * 1000 * 0.9,
  });
  if (!q.data) return null;
  return (
    <img
      src={q.data}
      alt=""
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        borderRadius: "var(--radius-card)",
      }}
    />
  );
}

function PreviewLightbox({
  url,
  mime,
  onClose,
}: {
  url: string;
  mime: string | null;
  onClose: () => void;
}) {
  const isImage = mime ? /^image\//i.test(mime) : false;
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(10,11,13,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "var(--space-4)",
      }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close preview"
        style={{
          position: "absolute",
          top: 16,
          right: 16,
          background: "var(--bg-2)",
          border: "1px solid var(--hair-2)",
          color: "var(--fg)",
          borderRadius: "var(--radius-chip)",
          padding: 6,
          cursor: "pointer",
          display: "inline-flex",
        }}
      >
        <X size={16} strokeWidth={1.5} />
      </button>
      {isImage ? (
        <img
          src={url}
          alt=""
          onClick={(e) => e.stopPropagation()}
          style={{
            maxWidth: "90vw",
            maxHeight: "90vh",
            objectFit: "contain",
            borderRadius: "var(--radius-card)",
          }}
        />
      ) : mime === "application/pdf" ? (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "min(90vw, 800px)",
            height: "85vh",
            borderRadius: "var(--radius-card)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <iframe
            src={url}
            title="PDF preview"
            style={{ flex: 1, border: "none", width: "100%", height: "100%" }}
          />
        </div>
      ) : (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{
            color: "var(--fg)",
            background: "var(--bg-2)",
            border: "1px solid var(--hair-2)",
            borderRadius: "var(--radius-card)",
            padding: "var(--space-3) var(--space-4)",
            textDecoration: "none",
          }}
        >
          Open file in new tab
        </a>
      )}
    </div>
  );
}
